// The API, as one thing every screen shares — TODO T17 (30 Aug 2026), narrowed at T30.
//
// A context rather than a module-level singleton, because the request function needs Clerk's
// `getToken` and that only exists inside `<ClerkProvider>`. It also keeps the screens honest:
// nothing below here can reach `fetch` on its own.
//
// What the context holds is the TRANSPORT, not a client object with a method per endpoint. Since
// T30 each domain module builds its own calls on top of it (`api/searches.ts`, `api/clients.ts`,
// …), so adding an endpoint touches one file that no other brain lane is editing.
import { useAuth } from '@clerk/react';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createRequest, createStreamRequest, type RequestFn, type StreamFn } from './core.ts';

const RequestContext = createContext<RequestFn | null>(null);
/** T66b: the streamed request, for the one call that answers as it happens (api/find.ts). */
const StreamContext = createContext<StreamFn | null>(null);

export function ApiProvider({
  baseUrl,
  release = null,
  children,
}: {
  baseUrl: string;
  /** T160: the release this bundle was built from, which each import sends (env.ts). */
  release?: string | null;
  children: ReactNode;
}) {
  const { getToken } = useAuth();

  const request = useMemo(
    // `getToken` is called per request, never captured: Clerk rotates the session token about
    // once a minute and this app polls for as long as a tab is open.
    () => createRequest({ baseUrl, getToken: () => getToken(), release }),
    [baseUrl, getToken, release],
  );

  const stream = useMemo(
    () => createStreamRequest({ baseUrl, getToken: () => getToken() }),
    [baseUrl, getToken],
  );

  return (
    <RequestContext.Provider value={request}>
      <StreamContext.Provider value={stream}>{children}</StreamContext.Provider>
    </RequestContext.Provider>
  );
}

export function useStreamRequest(): StreamFn {
  const stream = useContext(StreamContext);
  if (!stream) throw new Error('useStreamRequest outside ApiProvider');
  return stream;
}

export function useRequest(): RequestFn {
  const request = useContext(RequestContext);
  if (!request) throw new Error('useRequest outside ApiProvider');
  return request;
}
